// The plan limits at the foot of the sessions list, as today's app shows them
// (~/projects/poulpe-legacy/web/src/code/Limits.tsx): for Claude and for Codex, a
// small meter for each limit the vendor counts (5 hours, 7 days), with the
// share used and when it resets; or why nothing is known. A plan whose last
// whole report says it has no five-hour limit says so, muted. The readings come
// from the runner (Poulpe-489); a reading that is not fresh says how old it is,
// and a window past its reset is not drawn.
import { useEffect, useState } from 'react'
import type { Runner } from '../api/types'
import type { LimitReading, LimitWindow, PlanLimitsReply, VendorUsage } from '../api/planLimits'
import { ApiError } from '../api/client'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState } from '../store/context'
import { exactTime } from '../components/Time'

/** From this share used a meter warns, and from this one it is nearly spent. */
const LIMIT_WARN = 75
const LIMIT_HIGH = 90

/** A reading older than this says so. */
const AGED_MS = 2 * 60_000

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** Each window by today's name for it. */
const LABELS: Record<LimitWindow, string> = { five_hours: '5 hours', weekly: '7 days', weekly_fable: '7 days, Fable', weekly_opus: '7 days, Opus', weekly_sonnet: '7 days, Sonnet' }

/** Why a vendor's limits are not known, in today's words. */
const UNKNOWN = {
  claude: "Claude's limits are not known yet: Claude Code says them when a session started here runs.",
  codex: 'No Codex session on this Mac has recorded its limits.',
}

/** Today's span: `under a minute`, `12 min`, `2 h`, `2 h 5 min`, `3 d`, `3 d 4 h`. */
function spanText(ms: number, precise = false): string {
  const span = Math.max(0, ms)
  if (span < MINUTE) return precise ? `${Math.round(span / 1000)} s` : 'under a minute'
  if (span < HOUR) return `${Math.floor(span / MINUTE)} min`
  if (span < DAY) {
    const h = Math.floor(span / HOUR)
    const m = Math.floor((span % HOUR) / MINUTE)
    return precise && m ? `${h} h ${m} min` : `${h} h`
  }
  const d = Math.floor(span / DAY)
  const h = Math.floor((span % DAY) / HOUR)
  return precise && h ? `${d} d ${h} h` : `${d} d`
}

/** When a limit resets, from `now`: `resets in 3 d 4 h`, `resets in 2 h`; '' when not said, or past. */
export function resetText(resetsAt: number | null, now: number): string {
  if (resetsAt === null || resetsAt * 1000 <= now) return ''
  const span = resetsAt * 1000 - now
  return `resets in ${spanText(span, span >= DAY)}`
}

/** How old a reading is: `as of 2 h ago`; '' for a fresh one. */
export function asOfText(observedAt: number | null, now: number): string {
  if (observedAt === null || now - observedAt * 1000 < AGED_MS) return ''
  return `as of ${spanText(now - observedAt * 1000)} ago`
}

function level(used: number | null): 'ok' | 'warn' | 'high' {
  if (used === null) return 'ok'
  return used >= LIMIT_HIGH ? 'high' : used >= LIMIT_WARN ? 'warn' : 'ok'
}

const iso = (seconds: number) => new Date(seconds * 1000).toISOString()

function Meter({ reading, now }: { reading: LimitReading; now: number }) {
  const label = LABELS[reading.window]
  const percent = reading.used_percent
  const used = typeof percent === 'number' && Number.isFinite(percent) ? Math.max(0, Math.round(percent)) : null
  const resets = resetText(reading.resets_at, now)
  const title = reading.resets_at !== null ? `Resets ${exactTime(iso(reading.resets_at))}` : undefined
  return (
    <li className={`code-limit is-${level(used)}`} title={title}>
      <span className="code-limit-label">
        {label}
        {resets && <span className="code-limit-resets"> · {resets}</span>}
      </span>
      <span className="code-limit-used">{used === null ? '—' : `${used}%`}</span>
      <span
        className="progress-track code-limit-track"
        role="progressbar"
        aria-label={`${label} used`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={used === null ? undefined : Math.min(100, used)}
        aria-valuetext={used === null ? 'not known' : `${used}%`}
      >
        <span className="progress-fill" style={{ width: `${Math.min(100, used ?? 0)}%` }} />
      </span>
    </li>
  )
}

const ORDER: LimitWindow[] = ['five_hours', 'weekly', 'weekly_fable', 'weekly_opus', 'weekly_sonnet']

export function VendorLimits({ name, vendor, usage, now, aged = false }: { name: string; vendor: 'claude' | 'codex'; usage: VendorUsage | undefined; now: number; aged?: boolean }) {
  // A window past its reset no longer says what is used (Poulpe-583).
  const limits = (usage?.windows ?? []).filter((reading) => reading.resets_at === null || reading.resets_at * 1000 > now).sort((a, b) => ORDER.indexOf(a.window) - ORDER.indexOf(b.window))
  const asOf = limits.length ? Math.max(...limits.map((reading) => reading.observed_at)) : null
  const age = aged && limits.length > 0 ? asOfText(asOf, now) : ''
  // Said only when a whole report establishes it, in the five-hour meter's place (Poulpe-722).
  const noFiveHours = usage?.no_five_hour_limit === true && limits.length > 0 && !limits.some((reading) => reading.window === 'five_hours')
  return (
    <section className="code-vendor" aria-label={`${name} plan limits`}>
      <div className="code-vendor-head">
        <h3 className="code-vendor-name" title={asOf !== null ? `As of ${exactTime(iso(asOf))}` : undefined}>
          {name}
          {age && <span className="code-vendor-age"> · {age}</span>}
        </h3>
      </div>
      {limits.length > 0 ? (
        <ul className="code-limit-list">
          {noFiveHours && (
            <li className="code-limit is-absent">
              <span className="code-limit-label">No 5-hour limit</span>
            </li>
          )}
          {limits.map((reading) => (
            <Meter key={reading.window} reading={reading} now={now} />
          ))}
        </ul>
      ) : (
        <p className="code-limit-none">{UNKNOWN[vendor]}</p>
      )}
    </section>
  )
}

type Held = { scope: string; replies: Record<string, PlanLimitsReply | 'private'> }
/** Personal readings live only in this view, scoped to both member and workspace. */
export function PlanLimits({ runners }: { runners: readonly Runner[] | null }) {
  const { api } = useSessionsEnv()
  const { me, offline } = useBoardState()
  const member = me?.member?.id
  const scope = `${me?.workspace.id ?? ''}/${member ?? ''}`
  const [held, setHeld] = useState<Held>({ scope, replies: {} })
  const [now, setNow] = useState(() => Date.now() / 1000)
  useEffect(() => {
    let current = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const read = async () => {
      if (!api.planLimits || offline || !member) return
      const replies = await Promise.all((runners ?? []).filter(r => !r.revoked && r.connected).map(async r => {
        try { return [r.id, await api.planLimits!(r.id, member)] as const }
        catch (e) { return [r.id, e instanceof ApiError && e.status === 404 ? 'private' as const : { kind: 'unavailable' as const }] as const }
      }))
      if (!current) return
      setHeld({ scope, replies: Object.fromEntries(replies) })
      setNow(Date.now() / 1000)
      timer = setTimeout(() => { void read() }, 30_000)
    }
    void read()
    const clock = setInterval(() => setNow(Date.now() / 1000), 30_000)
    return () => { current = false; clearTimeout(timer); clearInterval(clock) }
  }, [api, runners, member, scope, offline])
  if (!api.planLimits || !member) return null
  const replies = held.scope === scope ? held.replies : {}
  const visible = (runners ?? []).filter(r => !r.revoked && replies[r.id] !== 'private')
  if (!visible.length) return null
  const clock = now * 1000
  return (
    <div className="code-limits">
      {visible.map((r) => {
        const reply = replies[r.id]
        const named = visible.length > 1 ? `${r.name}: ` : ''
        if (offline || !r.connected) return <p key={r.id} className="code-limit-none">{named}The plan limits are not known while its runner is offline.</p>
        if (reply === undefined) return <p key={r.id} className="code-limit-none">{named}Reading the plan limits…</p>
        if (reply === 'private') return null
        if (reply.kind === 'unavailable') return <p key={r.id} className="code-limit-none">{named}The plan limits could not be read.</p>
        const of = (vendor: 'claude' | 'codex') => reply.vendors.find((usage) => usage.vendor === vendor)
        return (
          <div key={r.id} className="code-limits-runner">
            {named && <h3 className="code-vendor-name">{r.name}</h3>}
            <VendorLimits name="Claude" vendor="claude" usage={of('claude')} now={clock} aged />
            <VendorLimits name="Codex" vendor="codex" usage={of('codex')} now={clock} />
          </div>
        )
      })}
    </div>
  )
}
